// Utilities for safely reading values from unknown/any types (e.g., JSON data)
/**
 * Type guard to check if a value is an object (not null, not array)
 */
export const isObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

/**
 * Type guard to check if a value is a string
 */
export const isString = (value: unknown): value is string => {
  return typeof value === "string";
};

/**
 * Type guard to check if a value is a boolean
 */
export const isBoolean = (value: unknown): value is boolean => {
  return typeof value === "boolean";
};

/**
 * Safely reads a boolean property from an unknown object
 * @param obj - The object to read from
 * @param key - The property key to read
 * @param fallback - Fallback value if property doesn't exist or isn't a boolean
 * @returns The boolean value or fallback
 */
export const readBooleanProperty = (
  obj: unknown,
  key: string,
  fallback: boolean | null,
): boolean | null => {
  if (!isObject(obj) || !(key in obj)) {
    return fallback;
  }

  const value = obj[key];
  return isBoolean(value) ? value : fallback;
};

/**
 * Safely reads a string property from an unknown object
 * @param obj - The object to read from
 * @param key - The property key to read
 * @param fallback - Fallback value if property doesn't exist or isn't a string
 * @returns The string value or fallback
 */
export const readStringProperty = (
  obj: unknown,
  key: string,
  fallback: string = "",
): string => {
  if (!isObject(obj) || !(key in obj)) {
    return fallback;
  }

  const value = obj[key];
  return isString(value) ? value : fallback;
};
